// Patička (SiteFooter) — design_handoff_paticka/README.md + SiteFooter.dc.html
// (světlá varianta 1b, 2026-08-25; nahrazuje screens/02-footer.md).
//
// Nativní patička Jiposu je mřížka admin bannerů (obsah předchůdce) + nativní
// newsletter, kontaktní box a Instagram widget. Patička má pět pásů pod sebou
// (hodnocení · IG · značky · 5 sloupců · copyright) — trust pás a pás dopravců
// a plateb z handoffu Tomáš 2026-08-25 zrušil, sekci hodnocení sem 2026-09-05
// naopak přesunul z homepage, ať stojí na celém webu (reviews.js). Takže:
//   - PŘEBÍRÁME nativní prvky, které nesou data: Instagram widget, newsletter form,
//     odkazy na sociální sítě z `.contact-box`, `#signature`, copyright a odkaz
//     na nastavení cookies (`a.js-cookies-settings` — na jeho třídě visí default JS),
//   - vlastní markup dodáváme tam, kde v DOM nic není (značky, sloupce odkazů,
//     zákaznický servis),
//   - bannery předchůdce skrývá CSS až pod `html.bs-footer-ready` (stejný pojistný
//     vzor jako hlavička: když JS spadne, zůstane funkční nativní patička).
//
// Idempotentní. Pokladna (`/kosik/`) patičku vůbec nerenderuje → modul jen odejde.

import { icon } from './icon.js';
import { fillAvatar } from './teamPhoto.js';
import { CONTACT, phonesOn, supportHoursText, telHref } from './headerTools.js';
import { mountReviews } from './reviews.js';
import { FAQ } from '../locale/cs/common.js';
import L from '../locale/cs/siteFooter.js';
import SHOP from '../../config/shop.js';
import SISTER from '../../config/sister.js';
import { flag, sisterLink } from './countrySwitch.js';
import { bodyClass, page, withPages } from './pages.js';
import { asChatTrigger, chatFallbackHref, chatOn } from './supportChat.js';
import LC from '../locale/cs/supportChat.js';

const NS = 'http://www.w3.org/2000/svg';

// ---------------------------------------------------------------------------
// Obsah (texty z designu, URL ověřené proti nativnímu menu a fixtures)
// ---------------------------------------------------------------------------

const IG = {
  // Nové znění 2026-08-31 (zadání Tomáše) — nahrazuje prototypové „Zůstaňte
  // v obraze a ve formě #jiposcz". Akcent (růžová z designu) nese klíčové
  // „jako první"; text se láme přirozeně podle šířky sloupce, žádný <br>.
  ...L.ig,
  fallbackHref: SHOP.instagram, // profil shopu z manifestu, když nativní widget chybí
  tiles: 6, // design má mřížku 6 čtverců, nativní widget dodává 8 fotek
};

// Wordmarky značek = `brands.footer` z manifestu shopu (proč GEKO místo
// designového „Boxer" a otevřený bod 2 handoffu viz tam).
const BRANDS_LABEL = L.brandsLabel;
const BRANDS = SHOP.brands.footer.map((b) => ({ label: b.label, href: page('brands', b.slug + '/') }));

// Sloupce odkazů. Nadpisy a barvy jsou z designu, položky kurátorované tak, aby
// každý odkaz vedl na existující stránku (stejný princip jako topBarLinks.js).
// Z designu vypadly cíle, které shop zatím nemá: „O nás", „Kariéra", „Video
// recenze", „Sleva pro věrné zákazníky" (info stránky = F5, issues #37/#38)
// a „Vaše oblíbené produkty" (wishlist se teprve zapíná, #35). „Nejčastěji se
// ptáte" se vrátilo 2026-09-15 se stránkou /caste-dotazy/, na pozici z designu.
const COLUMNS = [
  {
    eyebrow: L.colInfo.eyebrow,
    title: L.colInfo.title,
    modifier: 'info',
    links: withPages([
      { href: page('contact'), label: L.colInfo.contact },
      { href: FAQ.href, label: FAQ.footerLabel },
      { href: page('shipping'), label: L.colInfo.shipping },
      { href: page('claimsPolicy'), label: L.colInfo.claims },
      { href: page('withdrawal'), label: L.colInfo.withdrawal },
      { href: page('terms'), label: L.colInfo.terms },
      { href: page('privacy'), label: L.colInfo.privacy },
      { href: page('ratings'), label: L.colInfo.ratings },
    ]),
  },
  {
    eyebrow: L.colFirma.eyebrow,
    title: L.colFirma.title,
    modifier: 'firma',
    links: withPages([
      { href: page('store'), label: L.colFirma.store },
      { href: page('b2b'), label: L.colFirma.b2b },
      { href: page('blog', 'aktuality/'), label: L.colFirma.news },
      { href: page('brands'), label: L.colFirma.brands },
      { href: page('contact'), label: L.colFirma.contact },
      // Druhý shop firmy (#47; od 2026-10-05 část přepínače CZ ↔ SK,
      // countrySwitch.js): vlajka a cíl podle stránky, otevírá se ve stejném okně.
      { href: SISTER.origin + '/', label: SHOP.sisterShop.label, sister: true },
    ]),
  },
  {
    eyebrow: L.colRadce.eyebrow,
    title: L.colRadce.title,
    modifier: 'radce',
    links: withPages([
      { href: page('blog', 'rady-a-tipy/'), label: L.colRadce.tips },
      { href: page('blog', 'inspirace/'), label: L.colRadce.inspiration },
      { href: page('service'), label: L.colRadce.service },
      { href: page('orderStatus'), label: L.colRadce.orderStatus },
      { href: page('claim'), label: L.colRadce.claimForm },
      { href: page('account'), label: L.colRadce.account },
    ]),
  },
];

// Kontakt z manifestu shopu (`CONTACT`, `store.address`; = handoff §1). Karta
// „Zákaznický servis" nese pracovní dobu PODPORY (Po–Čt 8:00–15:00, Pá 7:30–14:30), ne
// otevírací dobu prodejny — do 2026-09-15
// tu visela prodejna a zákazník čekal odpověď i o víkendu. Text skládá
// `supportHoursText()` ze sdíleného rozpisu `CONTACT.supportHours` (headerTools.js).
const SERVICE = {
  // Mobilní karta (m02-footer.md §3) má delší popisek — dodatek se na desktopu
  // schová CSS, ať nemusíme držet dva texty ani dva uzly.
  ...L.service,
  hours: supportHoursText(),
  address: SHOP.store.address,
};

const NEWSLETTER = {
  ...L.newsletter,
  // veřejný endpoint seznamu Ecomailu z manifestu shopu (`newsletter.ecomailAction`)
  // — viz retargetNewsletter(); null = formulář zůstává nativní
  action: SHOP.newsletter.ecomailAction,
};

// Podpis autora šablony (#47, audit §2 bod 5). Rozhodnutí Tomáše 2026-08-27:
// nativní `#signature` se nerozebírá — dopisujeme se za něj; mi-ma.cz
// (předchůdce) vypadává. Znění od 2026-08-31 (zadání Tomáše): „Běží na
// Shoptet Premium · Shoptet šablony na míru khoder.cz" — nativní „Vytvořil"
// se přetextuje na „Běží na", odkaz i logo platformy zůstávají nativní.
// Od 2026-09-15 je odkazem celý text „Shoptet šablony na míru khoder.cz"
// a vede na landing šablon s UTM (zadání Tomáše).
const SIGN = {
  ...L.sign,
  href: 'https://www.khoder.cz/shoptet-premium-sablony-na-miru?utm_source=' + SHOP.shop.domain + '&utm_medium=referral',
};

const LEGAL = [
  { href: page('privacy'), label: L.legal.privacy },
  { href: page('terms'), label: L.legal.terms },
];

// Sociální ikony nejsou ve spritu (02-komponenty.md §19) — inline SVG z mockupu.
// Pořadí je dané designem, vykreslí se jen sítě, které shop v DOM opravdu má.
const SOCIAL = {
  facebook: {
    label: 'Facebook',
    size: 18,
    shapes: [['path', { d: 'M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.25-1.5 1.55-1.5H16.7V4.7c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.45-4 4.1v2.2H7.5V14h2.8v8z' }]],
  },
  instagram: {
    label: 'Instagram',
    size: 18,
    stroke: true,
    shapes: [
      ['rect', { x: 3, y: 3, width: 18, height: 18, rx: 5 }],
      ['circle', { cx: 12, cy: 12, r: 4 }],
      ['circle', { cx: 17.3, cy: 6.7, r: 1.1, fill: 'currentColor', stroke: 'none' }],
    ],
  },
  twitter: {
    label: 'X',
    size: 17,
    shapes: [['path', { d: 'M17.5 3h3.2l-7 8 8.2 11h-6.4l-5-6.6L4.8 22H1.6l7.5-8.6L1.2 3h6.6l4.5 6z' }]],
  },
  youtube: {
    label: 'YouTube',
    size: 20,
    shapes: [['path', { d: 'M22 12s0-3.2-.4-4.7a2.5 2.5 0 0 0-1.75-1.75C18.35 5.1 12 5.1 12 5.1s-6.35 0-7.85.45A2.5 2.5 0 0 0 2.4 7.3C2 8.8 2 12 2 12s0 3.2.4 4.7a2.5 2.5 0 0 0 1.75 1.75C5.65 18.9 12 18.9 12 18.9s6.35 0 7.85-.45A2.5 2.5 0 0 0 21.6 16.7C22 15.2 22 12 22 12zM10 15.1V8.9l5.2 3.1z' }]],
  },
  tiktok: {
    label: 'TikTok',
    size: 17,
    shapes: [['path', { d: 'M16.5 3c.3 2.3 1.6 3.9 3.9 4.2v2.8c-1.3.1-2.6-.3-3.9-1v6.4c0 3.6-2.9 6.1-6.1 5.6-3-.5-4.6-3.9-3.4-6.7.9-2 3-3 5-2.7v3c-.4-.1-.8-.2-1.2-.1-1.1.1-1.9 1-1.8 2.2.1 1.1 1 1.9 2.1 1.9 1.2 0 2.1-.9 2.1-2.2V3z' }]],
  },
};
const SOCIAL_ORDER = ['facebook', 'instagram', 'twitter', 'youtube', 'tiktok'];

// ---------------------------------------------------------------------------
// Helpery
// ---------------------------------------------------------------------------

function node(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

function link(href, className, text) {
  const a = node('a', className, text);
  a.href = href;
  return a;
}

// Eyebrow (11px verzálky) patří nad nadpis každého sloupce — design 1b.
function eyebrow(text) {
  return node('span', 'bs-footer-col__eyebrow', text);
}

// Inline SVG (sociální sítě) — sprite je jen pro ikony z 02-komponenty.md §19.
function socialSvg(spec) {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  if (spec.stroke) {
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.9');
  } else {
    svg.setAttribute('fill', 'currentColor');
  }
  svg.style.width = spec.size + 'px';
  svg.style.height = spec.size + 'px';
  for (const [tag, attrs] of spec.shapes) {
    const shape = document.createElementNS(NS, tag);
    for (const key in attrs) shape.setAttribute(key, attrs[key]);
    svg.appendChild(shape);
  }
  return svg;
}

// ---------------------------------------------------------------------------
// 1. Instagram pás — nativní widget + náš levý sloupec
// ---------------------------------------------------------------------------

// Sekce hodnocení = PRVNÍ pás patičky (zadání Tomáše 2026-09-05: „vezmi sekci
// na homepage a dej ji na celý web"). Na homepage tím zůstává na svém místě —
// stála poslední v obsahu, hned nad patičkou. Bez skóre se nevykreslí nic.
// Sekce stojí hned ze zálohy v manifestu, živá čísla a citace z API ji vymění
// (reviews.js `mountReviews`, recenze U2). Stránka hodnocení nese týž souhrn
// nahoře (ratingsPage.js, návrh S schválený Tomášem 7. 10. 2026), pás by tam
// byl podruhé.
function buildReviewsBand(footer) {
  if (document.body.classList.contains(bodyClass('ratings'))) return;
  mountReviews(footer);
}

function buildInstagram(footer) {
  const col = footer.querySelector('.custom-footer__instagram');
  if (!col || col.querySelector('.bs-footer-ig__intro')) return;
  col.classList.add('bs-footer-ig');

  const intro = node('div', 'bs-footer-ig__intro');

  // Nativní nadpis („Instagram") přetextujeme — zůstane h3 v hierarchii patičky.
  const title = col.querySelector('h3') || node('h3');
  title.classList.add('bs-footer-ig__title'); // nativní `.pageElement__heading` necháváme
  title.textContent = '';
  title.appendChild(document.createTextNode(IG.titleBefore));
  title.appendChild(node('span', 'bs-footer-ig__tag', IG.titleAccent));
  title.appendChild(document.createTextNode(IG.titleAfter));
  intro.appendChild(title);

  // Nativní tlačítko „Sledovat na Instagramu" má reálnou URL profilu → převezmeme
  // ho i s odkazem a jen předěláme na outline tlačítko z designu.
  const followWrap = col.querySelector('.instagram-follow-btn');
  const follow = followWrap ? followWrap.querySelector('a') : null;
  const cta = follow || link(IG.fallbackHref, null, null);
  cta.className = 'bs-footer-ig__cta';
  cta.textContent = '';
  cta.appendChild(socialSvg({ ...SOCIAL.instagram, size: 18 }));
  cta.appendChild(node('span', null, IG.cta));
  if (!cta.getAttribute('target')) cta.setAttribute('target', '_blank');
  cta.setAttribute('rel', 'noopener');
  intro.appendChild(cta);
  if (followWrap) followWrap.remove();

  col.insertBefore(intro, col.firstChild);

  // Design má Instagram jako tmavý pás přes CELOU šířku (#101114) s containerem
  // uvnitř — nativně sedí uvnitř `.footer-rows`, tak ho přesuneme před ni.
  const band = node('div', 'bs-footer-band bs-footer-band--ig');
  const inner = node('div', 'container');
  band.appendChild(inner);
  inner.appendChild(col);
  const rows = footer.querySelector('.footer-rows');
  if (rows) footer.insertBefore(band, rows);
  else footer.appendChild(band);
}

// ---------------------------------------------------------------------------
// 2. Pás značek
// ---------------------------------------------------------------------------

function buildBrands() {
  const row = node('div', 'bs-footer-brands');
  row.appendChild(node('span', 'bs-footer-brands__label', BRANDS_LABEL));
  const list = node('div', 'bs-footer-brands__list');
  for (const brand of BRANDS) {
    list.appendChild(link(brand.href, 'bs-footer-brands__item', brand.label));
  }
  row.appendChild(list);
  return row;
}

// ---------------------------------------------------------------------------
// 3. Hlavní sloupce (3× odkazy + servis + nativní newsletter)
// ---------------------------------------------------------------------------

// `--links` = čistě odkazový sloupec; na mobilu z něj footerAccordion.js udělá
// akordeon (servis a newsletter zůstávají otevřené — m02-footer.md).
function buildLinkColumn(column) {
  const col = node('div', 'bs-footer-col bs-footer-col--links bs-footer-col--' + column.modifier);
  col.appendChild(eyebrow(column.eyebrow));
  col.appendChild(node('h3', 'bs-footer-col__title', column.title));
  const list = node('div', 'bs-footer-col__links');
  for (const item of column.links) {
    const a = link(item.href, 'bs-footer-col__link', item.label);
    if (item.sister) {
      a.classList.add('bs-footer-col__link--sister');
      sisterLink(a);
      a.insertBefore(flag(SISTER.country), a.firstChild);
    }
    list.appendChild(a);
  }
  col.appendChild(list);
  return col;
}

function buildServiceColumn() {
  const col = node('div', 'bs-footer-col bs-footer-col--servis');
  col.appendChild(eyebrow(SERVICE.eyebrow));
  col.appendChild(node('h3', 'bs-footer-col__title', SERVICE.title));

  // Obal těla sloupce. Na desktopu je `display: contents` (layout beze změny),
  // na mobilu z něj CSS udělá kartu #16181D z FooterMobil (m02-footer.md §3).
  const card = node('div', 'bs-footer-service__card');

  const head = node('div', 'bs-footer-service__head');
  // Foto poradkyně (image-slot foot-agent), fallback ikonka sluchátek.
  const photo = node('span', 'bs-footer-service__photo');
  fillAvatar(photo, 'footer', 56);
  head.appendChild(photo);
  const headText = node('div', 'bs-footer-service__headtext');
  const lead = node('strong', 'bs-footer-service__lead', SERVICE.lead);
  lead.appendChild(node('span', 'bs-footer-service__lead-more', SERVICE.leadMore));
  headText.appendChild(lead);
  headText.appendChild(node('span', 'bs-footer-service__sub', SERVICE.sub));
  head.appendChild(headText);
  card.appendChild(head);

  // Kanály v pořadí chat → e-mail → telefon: se zapnutým chatem nese velký
  // řádek chat a telefon (je-li zapnutý) ustoupí pod e-mail v jeho stylu
  const chat = chatOn();
  if (chat) {
    const chatBtn = link(chatFallbackHref(), 'bs-footer-service__phone bs-footer-service__chat');
    chatBtn.appendChild(icon('chat'));
    chatBtn.appendChild(node('span', null, LC.cta));
    asChatTrigger(chatBtn, 'footer');
    card.appendChild(chatBtn);
  } else if (phonesOn()) {
    const phone = link(telHref(CONTACT.phone), 'bs-footer-service__phone');
    phone.appendChild(icon('phone'));
    phone.appendChild(node('span', null, CONTACT.phone));
    card.appendChild(phone);
  }

  card.appendChild(node('div', 'bs-footer-service__hours', SERVICE.hours));

  const mail = link('mailto:' + CONTACT.email, 'bs-footer-service__mail');
  mail.appendChild(icon('mail'));
  mail.appendChild(node('span', null, CONTACT.email));
  card.appendChild(mail);

  if (chat && phonesOn()) {
    const phone = link(telHref(CONTACT.phone), 'bs-footer-service__mail');
    phone.appendChild(icon('phone'));
    phone.appendChild(node('span', null, CONTACT.phone));
    card.appendChild(phone);
  }

  const address = node('div', 'bs-footer-service__address');
  address.appendChild(icon('pin'));
  address.appendChild(node('span', null, SERVICE.address));
  card.appendChild(address);

  col.appendChild(card);
  return col;
}

// Sítě bereme z nativního DOM (contact-box + Instagram widget), ať v patičce
// nesvítí odkaz na profil, který shop nemá.
function collectSocial(footer) {
  const found = new Map();
  const remember = (key, href) => {
    if (key && href && !found.has(key)) found.set(key, href);
  };

  for (const a of footer.querySelectorAll('.contact-box .social-icon')) {
    const key = SOCIAL_ORDER.find((name) => a.classList.contains(name));
    remember(key, a.getAttribute('href'));
  }

  const igCta = footer.querySelector('.bs-footer-ig__cta');
  remember('instagram', igCta && igCta.getAttribute('href'));

  return SOCIAL_ORDER.filter((key) => found.has(key)).map((key) => ({
    key,
    href: found.get(key),
    spec: SOCIAL[key],
  }));
}

function buildSocialRow(footer) {
  const items = collectSocial(footer);
  if (!items.length) return null;

  const wrap = node('div', 'bs-footer-social');
  wrap.appendChild(node('div', 'bs-footer-social__label', NEWSLETTER.socialLabel));
  const list = node('div', 'bs-footer-social__list');
  for (const item of items) {
    const a = link(item.href, 'bs-footer-social__item');
    a.setAttribute('aria-label', item.spec.label);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener');
    a.appendChild(socialSvg(item.spec));
    list.appendChild(a);
  }
  wrap.appendChild(list);
  return wrap;
}

// Nativní newsletter form (`#formNewsletterWidget`) jen přeskládáme do pilulky
// z designu: input + odesílací tlačítko do jednoho wrapperu, souhlas zůstává pod ním.
function restyleNewsletter(col, footer) {
  col.classList.add('bs-footer-col', 'bs-footer-col--newsletter');

  const heading = col.querySelector('.newsletter-header h3');
  if (heading) {
    heading.classList.add('bs-footer-col__title');
    heading.textContent = NEWSLETTER.title;
    if (!col.querySelector('.bs-footer-col__eyebrow')) heading.before(eyebrow(NEWSLETTER.eyebrow));
  }

  // Nativní perex v .newsletter-header by se dubloval s naším descem.
  const nativeDesc = col.querySelector('.newsletter-header p');
  if (nativeDesc) nativeDesc.remove();

  if (!col.querySelector('.bs-footer-newsletter-desc')) {
    const desc = node('p', 'bs-footer-newsletter-desc', NEWSLETTER.desc);
    const header = col.querySelector('.newsletter-header');
    if (header) header.after(desc);
    else col.prepend(desc);
  }

  const form = col.querySelector('form.subscribe-form');
  if (form) retargetNewsletter(form);
  if (form && !form.querySelector('.bs-nl__pill')) {
    const button = form.querySelector('button[type="submit"]');
    const input = form.querySelector('input[type="email"]');
    // Shoptet 2026-08 přeznačil markup: .validator-msg-holder zmizel, input
    // žije ve .form-group.smart-label-wrapper > .form-control-wrapper.
    // Pilulku stavíme z libovolného wrapperu, který input obaluje.
    const holder =
      form.querySelector('.validator-msg-holder') ||
      (input && input.closest('.form-group')) ||
      null;
    if (holder && button) {
      const pill = node('div', 'bs-nl__pill');
      holder.before(pill);
      pill.appendChild(holder);
      pill.appendChild(button);
    }
    if (input) input.placeholder = NEWSLETTER.placeholder;
    if (button) {
      // Nativní popisek tlačítka („Přihlásit se") bývá sr-only, od 2026-08 je
      // ale viditelný a lepil se k našemu textu — holé textové uzly pryč,
      // viditelný zůstává jen popisek z designu.
      for (const child of [...button.childNodes]) {
        if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) child.remove();
      }
      if (!button.querySelector('.bs-nl__label')) {
        button.appendChild(node('span', 'bs-nl__label', NEWSLETTER.submit));
      }
    }
  }

  const social = buildSocialRow(footer);
  if (social) col.appendChild(social);
}

// Cíl formuláře = Ecomail napřímo (zadání správce Ecomailu 2026-09-02, rozhodl
// Tomáš): nativní `POST /action/MailForm/subscribeToNewsletters/` ukládal
// adresu do Shoptetu bez potvrzení (robot přihlásí cizí adresu) a do Ecomailu
// ji nosil doplněk. Klasický formulářový POST na veřejný endpoint seznamu:
// Ecomail spustí double opt-in a sběr přestane záviset na doplňku. Vzhled se
// nemění; ruší se jen pole, která patří Shoptetu (formId, honeypot `surname`
// s sr-only nápovědou, hidden consents[]) — Ecomail čte `email` a vlastní
// honeypoty (`e-mail`, `firstname`) si nese sám. Shoptetí validátor
// (`js-validate` na inputu) běží dál, prázdný/nevalidní e-mail neodejde;
// CSRF listener default JS se formu netýká (nemá `csrf-enabled`). Do Shoptetu
// touhle cestou adresy nepřibývají — chtěné, zdroj pravdy je Ecomail.
// Pryč jde i captcha Shoptetu (dev od 5. 10. 2026: vnořený fieldset „Bezpečnostní
// kontrola" s povinným polem `captcha`): ověřuje ji jen nativní endpoint, Ecomail
// ji zahodí a proti robotům má double opt-in a honeypoty. Zůstala by-li, validátor
// Shoptetu odeslání zastaví a přihláška tiše neodejde (QA newsletter-live).
function retargetNewsletter(form) {
  if (!NEWSLETTER.action) return;
  removeShoptetCaptcha(form);
  if (form.dataset.bsTarget) return;
  form.dataset.bsTarget = 'ecomail';
  form.setAttribute('action', NEWSLETTER.action);
  form.setAttribute('method', 'post');
  const shoptetOnly = 'input[name="formId"], input[name="surname"], input[name="consents[]"], span.no-display';
  for (const el of form.querySelectorAll(shoptetOnly)) el.remove();
}

// Celý vnořený fieldset s captchou, ne vnější fieldset formu s e-mailem;
// bez vlastního fieldsetu aspoň skupina pole a obrázek. Idempotentní (re-init).
function removeShoptetCaptcha(form) {
  for (const input of form.querySelectorAll('input[name="captcha"]')) {
    const box = input.closest('fieldset');
    if (box && form.contains(box) && !box.querySelector('input[name="email"]')) box.remove();
    else (input.closest('.form-group') || input).remove();
  }
  for (const el of form.querySelectorAll('.captcha-image')) el.remove();
}

function buildColumns(footer, rows) {
  const wrap = node('div', 'bs-footer-cols');
  for (const column of COLUMNS) wrap.appendChild(buildLinkColumn(column));
  wrap.appendChild(buildServiceColumn());

  const newsletter = footer.querySelector('.custom-footer__newsletter');
  if (newsletter) {
    restyleNewsletter(newsletter, footer);
    wrap.appendChild(newsletter); // přesun z nativní mřížky do našeho gridu
  }

  rows.appendChild(wrap);
}

// ---------------------------------------------------------------------------
// 4. Copyright — nativní `.footer-bottom`, doplněné právní odkazy
// ---------------------------------------------------------------------------

function buildBottom(footer) {
  const bottom = footer.querySelector('.footer-bottom .container');
  if (!bottom || bottom.querySelector('.bs-footer-legal')) return;

  const legal = node('nav', 'bs-footer-legal');
  legal.setAttribute('aria-label', L.legal.aria);
  for (const item of LEGAL) {
    legal.appendChild(link(item.href, 'bs-footer-legal__link', item.label));
  }

  // Odkaz na nastavení cookies je nativní (default JS visí na `.js-cookies-settings`)
  // — jen ho přesuneme z odstavce copyrightu do řádku právních odkazů.
  const cookies = bottom.querySelector('a.js-cookies-settings');
  if (cookies) {
    cookies.classList.add('bs-footer-legal__link');
    legal.appendChild(cookies);
  }

  bottom.appendChild(legal);

  // `#signature` („Vytvořil Shoptet Premium") je povinný prvek platformy —
  // nerozebíráme ho ani nenahrazujeme, jen za nativní obsah DOVNITŘ dopíšeme
  // druhého autora (#47). Uvnitř proto, že Shoptet drží na uzlu inline
  // `display: inline-block !important`: samostatný sourozenec by v pásu byl
  // další flex položkou (další `gap`, jiná výška řádku a rozbitá parita).
  const signature = bottom.querySelector('#signature');
  if (signature && !signature.querySelector('.bs-footer-sign__link')) {
    // „Vytvořil" → „Běží na": mění se JEN textový uzel, odkaz a logo Shoptetu
    // zůstávají netknuté (povinný prvek platformy se dál nerozebírá). Text leží
    // UVNITŘ `a.title` („Vytvořil Shoptet Premium"), ne jako přímý potomek —
    // do 2026-09-15 se proto procházely jen childNodes a na webu přepis nikdy
    // neproběhl.
    const walker = document.createTreeWalker(signature, NodeFilter.SHOW_TEXT);
    for (let t = walker.nextNode(); t; t = walker.nextNode()) {
      if (t.textContent.indexOf(SIGN.nativeFrom) !== -1) {
        t.textContent = t.textContent.replace(SIGN.nativeFrom, SIGN.nativeTo);
      }
    }
    signature.appendChild(document.createTextNode(SIGN.sep));
    signature.appendChild(link(SIGN.href, 'bs-footer-sign__link', SIGN.label));
  }
}

// ---------------------------------------------------------------------------

export function initSiteFooter() {
  const footer = document.getElementById('footer');
  if (!footer || footer.dataset.bsFooter) return;
  const rows = footer.querySelector('.footer-rows');
  if (!rows) return;
  footer.dataset.bsFooter = '1';

  buildReviewsBand(footer);
  buildInstagram(footer);
  rows.appendChild(buildBrands());
  buildColumns(footer, rows);

  buildBottom(footer);

  // Naše patička stojí → teprve teď smí CSS skrýt bannery předchůdce.
  document.documentElement.classList.add('bs-footer-ready');
}
